import React, { useCallback, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Z_INDEX } from '@/constants/layers'
import ErrorBanner from '@/components/ErrorBanner'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ModalProps {
  isOpen: boolean
  /** Kapatma istegi (backdrop, X, ESC, iptal). `isSubmitting` iken yok sayilir. */
  onClose: () => void
  title?: string | undefined
  children: React.ReactNode
  /** Alt aksiyon serisi (Kaydet / Iptal). */
  footer?: React.ReactNode | undefined
  /**
   * Modal ici hata. Baslik ile govde ARASINDA gosterilir.
   * Sayfa govdesindeki banner modal backdrop'unun altinda kaldigi icin
   * modal acikken olusan her hata BURAYA verilmelidir.
   */
  error?: string | null | undefined
  onDismissError?: (() => void) | undefined
  /** true iken ESC/backdrop ile kapatma engellenir (istek ucusta). */
  isSubmitting?: boolean | undefined
  size?: ModalSize | undefined
  closeOnBackdrop?: boolean | undefined
  /** Panel icin ek sinif (ozel genislik/padding gerektiginde). */
  panelClassName?: string | undefined
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

/**
 * Tum modallarin ortak tabani.
 *
 * Neden: projede 17 ayri modal kendi `fixed inset-0` overlay'ini, kendi ESC/backdrop
 * mantigini ve kendi z-index'ini yaziyordu. Sonuc: 17 modaldan yalnizca 1'i hata
 * gosterebiliyordu, digerlerinde hata sayfa arkasinda gorunmez kaliyordu.
 * Hata slotu bilesenin ICINDE oldugu icin "modal acikken hata modalda" kurali
 * bu tabani kullanan her modalda otomatik saglanir.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  error,
  onDismissError,
  isSubmitting = false,
  size = 'md',
  closeOnBackdrop = true,
  panelClassName = '',
}) => {
  const errorRef = useRef<HTMLDivElement | null>(null)

  // Panel `max-h-[90vh] overflow-y-auto`: uzun bir formda kullanici alta inip
  // Kaydet'e basar, hata ise panelin EN USTUNDE belirir -- yani gorus alaninin
  // disinda. Kullanici acisindan "hicbir sey olmadi" demektir; duzeltmeye
  // calistigimiz sorunun ta kendisi. Hata gelince gorunur alana kaydiriyoruz.
  useEffect(() => {
    if (!isOpen || !error) return
    errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [isOpen, error])

  const requestClose = useCallback(() => {
    // Istek ucustayken kapatma: kullanici sonucu (ozellikle hatayi) goremez.
    if (isSubmitting) return
    onClose()
  }, [isSubmitting, onClose])

  // ESC ile kapatma -- her modalda ayri ayri yazilmis onKeyDown/tabIndex yerine.
  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') requestClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, requestClose])

  // Modal acikken arka planin kaymasini engelle.
  useEffect(() => {
    if (!isOpen) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: Z_INDEX.modal }}
          onClick={closeOnBackdrop ? requestClose : undefined}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`bg-white rounded-xl p-8 w-full ${SIZE_CLASSES[size]} max-h-[90vh] overflow-y-auto relative ${panelClassName}`}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={isSubmitting}
                  className="text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Kapat"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            )}

            {error && (
              <div ref={errorRef}>
                <ErrorBanner message={error} onDismiss={onDismissError} className="mb-6" />
              </div>
            )}

            {children}

            {footer && <div className="mt-6">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default Modal
